import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';
import { RolesService } from '../../../../core/services/user/roles.service';
import { forkJoin } from 'rxjs';
import { UserRole } from '../../../../../enums/roles.enum';
import { Organization } from 'app/core/models/organization/organization.model';
import { OrganizationService } from 'app/core/services/organization/organization.service';
import { UserSessionModel } from 'app/core/models/user/current-user.model';
import { CurrentUserService } from 'app/core/services/auth/current-user.service';
import { ClaimItem } from 'app/core/models/role/claim-item.model';
import { ServiceGroup } from 'app/core/models/role/service-group.model';
import { PermissionItem } from 'app/core/models/role/permission-item.model';
import { PermissionSubGroup } from 'app/core/models/role/permission-sub-group.model';
import { SelectComponent } from 'app/shared/components/select/select.component';

@Component({
  selector: 'app-role-form',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SelectComponent],
  templateUrl: './role-form.component.html',
  styleUrls: ['./role-form.component.scss'],
})
export class RoleFormComponent implements OnInit {
  mode: 'create' | 'edit' = 'create';
  roleId: string = '';
  roleName: string = '';
  serviceGroups: ServiceGroup[] = [];
  isLoading: boolean = true;
  isSaving: boolean = false;

  roleTypes: { label: string; value: number; isSelected: boolean }[] = [];
  private allClaims: ClaimItem[] = [];

  expandedServices: Set<string> = new Set();
  organizations: Organization[] = [];
  currentUser: UserSessionModel | null = null;
  selectedOrganizationId: string | null = null;
  orgSelectTouched: boolean = false;

  constructor(
    private translate: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private rolesService: RolesService,
    private organizationService: OrganizationService,
    private currentUserService: CurrentUserService
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
  }

  ngOnInit(): void {
    var id = this.route.snapshot.paramMap.get('id') || '';
    if (id) {
      this.mode = 'edit';
      this.roleId = id;
      this.initializeRoleTypes();
      this.loadRoleData();
    } else {
      this.mode = 'create';
      this.initializeRoleTypes();
      this.loadClaimsForCreate();
      this.currentUser = this.currentUserService.GetSession();

      const userOrgId =
        this.currentUser?.userOrganizations?.[0]?.organizationId;
      if (userOrgId) {
        this.selectedOrganizationId = userOrgId;
      } else {
        this.loadOrganizations();
      }
    }
  }

  loadOrganizations(): void {
    this.organizationService.getAllOrganizationItems().subscribe({
      next: (items) => {
        this.organizations = items;
      },
    });
  }

  initializeRoleTypes(): void {
    const roleLabels: { [key: number]: string } = {
      [UserRole.Nurse]: 'Nurse',
      [UserRole.OrganizationAdmin]: 'Organization Admin',
      [UserRole.FacilityAdmin]: 'Facility Admin',
      [UserRole.Specialist]: 'Specialist',
    };

    this.roleTypes = Object.keys(roleLabels)
      .map((key) => parseInt(key))
      .map((roleValue) => ({
        label: roleLabels[roleValue],
        value: roleValue,
        isSelected: false,
      }));
  }

  loadClaimsForCreate(): void {
    this.isLoading = true;
    this.rolesService.getListClaim().subscribe({
      next: (result) => {
        const allClaims: ClaimItem[] =
          result.data?.items?.map((item: any) => item.claim || item) || [];
        this.allClaims = allClaims;
        const roleClaimNames = [
          'organization_admin_claim',
          'facility_admin_claim',
          'nurse_claim',
          'specialist_claim',
        ];
        const filteredClaims = allClaims.filter(
          (claim) => !roleClaimNames.includes(claim.name)
        );

        this.serviceGroups = this.buildServiceGroups(filteredClaims, []);
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }

  loadRoleData(): void {
    this.isLoading = true;

    forkJoin({
      claims: this.rolesService.getListClaim(),
      role: this.rolesService.getRoleById(this.roleId),
    }).subscribe({
      next: (result) => {
        const allClaims: ClaimItem[] =
          result.claims.data?.items?.map((item: any) => item.claim || item) ||
          [];
        this.allClaims = allClaims;
        const roleClaimNames = [
          'organization_admin_claim',
          'facility_admin_claim',
          'nurse_claim',
          'specialist_claim',
        ];
        const filteredClaims = allClaims.filter(
          (claim) => !roleClaimNames.includes(claim.name)
        );

        const roleData = result.role.data?.role;
        this.roleName = roleData?.name || '';
        const roleClaims = roleData?.roleClaims || [];
        const selectedClaimIds = roleClaims.map((rc: any) => rc.claim?.id);

        const existingRoleClaim = roleClaims.find((rc: any) =>
          roleClaimNames.includes(rc.claim?.name)
        );
        if (existingRoleClaim) {
          let roleValue = 0;
          switch (existingRoleClaim.claim?.name) {
            case 'nurse_claim':
              roleValue = UserRole.Nurse;
              break;
            case 'organization_admin_claim':
              roleValue = UserRole.OrganizationAdmin;
              break;
            case 'facility_admin_claim':
              roleValue = UserRole.FacilityAdmin;
              break;
            case 'specialist_claim':
              roleValue = UserRole.Specialist;
              break;
          }
          if (roleValue > 0) {
            this.roleTypes.forEach(
              (r) => (r.isSelected = r.value === roleValue)
            );
          }
        }

        this.serviceGroups = this.buildServiceGroups(
          filteredClaims,
          selectedClaimIds
        );
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading role data:', err);
        this.toastService.showToast(
          this.translate.instant('roleList.errorLoadingRole'),
          'error'
        );
        this.isLoading = false;
      },
    });
  }

  buildServiceGroups(
    allClaims: ClaimItem[],
    selectedClaimIds: string[]
  ): ServiceGroup[] {
    const serviceMap: {
      [serviceName: string]: { [groupName: string]: PermissionItem[] };
    } = {};

    allClaims.forEach((claim) => {
      const serviceName = claim.serviceName || 'Other';
      const groupName = claim.groupName || 'General';

      let label = claim.name.replace(/_/g, ' ').replace('claim', '').trim();
      label = label.replace(/\b\w/g, (l) => l.toUpperCase());

      if (!serviceMap[serviceName]) {
        serviceMap[serviceName] = {};
      }
      if (!serviceMap[serviceName][groupName]) {
        serviceMap[serviceName][groupName] = [];
      }

      const selectedRoles = this.roleTypes
        .filter((r) => r.isSelected)
        .map((r) => r.value);
      const isDisabled =
        selectedRoles.length > 0 &&
        !selectedRoles.some((role) => claim.allowedRoles.includes(role));
      const isSelected =
        selectedRoles.length > 0
          ? selectedClaimIds.includes(claim.id) &&
            selectedRoles.some((role) => claim.allowedRoles.includes(role))
          : selectedClaimIds.includes(claim.id);

      serviceMap[serviceName][groupName].push({
        id: claim.id,
        key: claim.name,
        label: label,
        isSelected: isSelected,
        isDisabled: isDisabled,
        allowedRoles: claim.allowedRoles,
      });
    });

    const sortOrder = ['Identity', 'Organization', 'Patient'];

    const result = Object.keys(serviceMap).map((serviceName) => ({
      serviceName: serviceName,
      isExpanded: true,
      subGroups: Object.keys(serviceMap[serviceName]).map((groupName) => ({
        groupName: groupName,
        isExpanded: false,
        permissions: serviceMap[serviceName][groupName],
      })),
    }));

    return result.sort((a, b) => {
      const indexA = sortOrder.indexOf(a.serviceName);
      const indexB = sortOrder.indexOf(b.serviceName);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.serviceName.localeCompare(b.serviceName);
    });
  }

  toggleRoleType(roleType: any, event: any): void {
    if (this.mode === 'edit') {
      return;
    }

    const isChecked = event.target.checked;
    const currentClaims = this.getAllClaimsFromServiceGroups();
    let selectedClaimIds: string[] = [];

    if (isChecked) {
      this.roleTypes.forEach((r) => {
        r.isSelected = r.value === roleType.value;
      });

      selectedClaimIds = currentClaims
        .filter((claim) => claim.allowedRoles?.includes(roleType.value))
        .map((claim) => claim.id);
    } else {
      roleType.isSelected = false;

      selectedClaimIds = this.serviceGroups
        .flatMap((service) => service.subGroups)
        .flatMap((subGroup) => subGroup.permissions)
        .filter((p) => p.isSelected)
        .map((p) => p.id);
    }

    this.serviceGroups = this.buildServiceGroups(
      currentClaims,
      selectedClaimIds
    );
  }

  isRoleTypeDisabled(type: any): boolean {
    if (this.mode !== 'edit') return false;
    return !type.isSelected;
  }

  getAllClaimsFromServiceGroups(): ClaimItem[] {
    const claims: ClaimItem[] = [];
    this.serviceGroups.forEach((service) => {
      service.subGroups.forEach((subGroup) => {
        subGroup.permissions.forEach((perm) => {
          claims.push({
            id: perm.id,
            name: perm.key,
            serviceName: service.serviceName,
            groupName: subGroup.groupName,
            allowedRoles: perm.allowedRoles,
          });
        });
      });
    });
    return claims;
  }

  toggleServiceAccordion(service: ServiceGroup): void {
    service.isExpanded = !service.isExpanded;
  }

  toggleSubGroupAccordion(subGroup: PermissionSubGroup): void {
    subGroup.isExpanded = !subGroup.isExpanded;
  }

  toggleService(service: ServiceGroup, event: any): void {
    event.stopPropagation();
    const isChecked = event.target.checked;
    service.subGroups.forEach((subGroup) => {
      subGroup.permissions.forEach((p) => {
        if (!p.isDisabled) {
          p.isSelected = isChecked;
        }
      });
    });
  }

  toggleSubGroup(subGroup: PermissionSubGroup, event: any): void {
    event.stopPropagation();
    const isChecked = event.target.checked;
    subGroup.permissions.forEach((p) => {
      if (!p.isDisabled) {
        p.isSelected = isChecked;
      }
    });
  }

  isServiceFullySelected(service: ServiceGroup): boolean {
    const enabledPermissions = service.subGroups
      .flatMap((subGroup) => subGroup.permissions)
      .filter((p) => !p.isDisabled);

    return (
      enabledPermissions.length > 0 &&
      enabledPermissions.every((p) => p.isSelected)
    );
  }

  isSubGroupFullySelected(subGroup: PermissionSubGroup): boolean {
    const enabledPerms = subGroup.permissions.filter((p) => !p.isDisabled);
    return enabledPerms.length > 0 && enabledPerms.every((p) => p.isSelected);
  }

  getSelectedPermissionCount(service: ServiceGroup): number {
    return service.subGroups
      .flatMap((sg) => sg.permissions)
      .filter((p) => p.isSelected && !p.isDisabled).length;
  }

  getTotalPermissionCount(service: ServiceGroup): number {
    return service.subGroups
      .flatMap((sg) => sg.permissions)
      .filter((p) => !p.isDisabled).length;
  }

  isFormValid(): boolean {
    const hasRoleSelected = this.roleTypes.some((r) => r.isSelected);
    const hasRoleName = this.roleName.trim().length > 0;

    const hasOrganization =
      this.mode === 'edit' ||
      (this.selectedOrganizationId !== null &&
        this.selectedOrganizationId !== undefined &&
        this.selectedOrganizationId.trim().length > 0);

    return hasRoleSelected && hasRoleName && hasOrganization;
  }

  saveRole(): void {
    if (!this.isFormValid()) {
      this.toastService.showToast(
        this.translate.instant('roleList.roleNameAndTypeRequired'),
        'warning'
      );
      return;
    }

    this.isSaving = true;

    const selectedClaimIds: string[] = [];
    this.serviceGroups.forEach((service) => {
      service.subGroups.forEach((subGroup) => {
        subGroup.permissions.forEach((perm) => {
          if (perm.isSelected) {
            selectedClaimIds.push(perm.id);
          }
        });
      });
    });

    const selectedRole = this.roleTypes.find((r) => r.isSelected);
    if (selectedRole) {
      let roleClaimName = '';
      switch (selectedRole.value) {
        case UserRole.Nurse:
          roleClaimName = 'nurse_claim';
          break;
        case UserRole.OrganizationAdmin:
          roleClaimName = 'organization_admin_claim';
          break;
        case UserRole.FacilityAdmin:
          roleClaimName = 'facility_admin_claim';
          break;
        case UserRole.Specialist:
          roleClaimName = 'specialist_claim';
          break;
      }

      if (roleClaimName) {
        const roleClaim = this.allClaims.find((c) => c.name === roleClaimName);
        if (roleClaim) {
          selectedClaimIds.push(roleClaim.id);
        }
      }
    }

    const selectedOrganization = this.organizations.find(
      (o) => o.id === this.selectedOrganizationId
    );

    const request =
      this.mode === 'edit'
        ? this.rolesService.updateRole(
            this.roleId,
            this.roleName,
            selectedClaimIds
          )
        : this.rolesService.createRole({
            name: this.roleName,
            organizationId: this.selectedOrganizationId,
            organizationName: selectedOrganization?.name ?? null,
            claimIds: selectedClaimIds,
          });

    request.subscribe({
      next: (res) => {
        const message =
          this.mode === 'edit'
            ? 'roleList.roleUpdatedSuccess'
            : 'roleList.roleCreatedSuccess';

        this.toastService.showToast(this.translate.instant(message), 'success');
        this.isSaving = false;
        this.router.navigate(['/role/list']);
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/role/list']);
  }

  isServiceDisabled(service: ServiceGroup): boolean {
    const hasAnyEnabledPermission = service.subGroups.some((subGroup) =>
      subGroup.permissions.some((p) => !p.isDisabled)
    );

    return !hasAnyEnabledPermission;
  }

  isSubGroupDisabled(subGroup: PermissionSubGroup): boolean {
    return !subGroup.permissions.some((p) => !p.isDisabled);
  }
}
